import * as React from 'react'
import {
  usePlantSessionPaint,
  type PlantSessionGhost,
  type PlantSessionMarkEntry,
  type PlantSessionMarkView
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { cn } from '../../lib/utils'

/** Tile/row shell classes for session paint. */
export function sessionMarkClass(base: string, mark: PlantSessionMarkView | undefined): string {
  return cn(
    base,
    mark === 'added' && 'border-st-run/40 bg-st-run-bg',
    mark === 'touched' && 'border-st-warn/40 bg-st-warn-bg',
    mark === 'dimmed' && 'border-line bg-bg-sunk/70'
  )
}

/** Detail-page header shell: tint the title block when this entity was painted. */
export function sessionDetailHeaderClass(
  base: string,
  mark: PlantSessionMarkView | undefined
): string {
  return cn(
    base,
    mark === 'added' && 'rounded-xl border border-st-run/40 bg-st-run-bg p-3',
    mark === 'touched' && 'rounded-xl border border-st-warn/40 bg-st-warn-bg p-3',
    mark === 'dimmed' && 'rounded-xl border border-line bg-bg-sunk/70 p-3'
  )
}

export function SessionNewBadge(): React.ReactElement {
  return (
    <Badge variant="outline" className="border-st-run/40 bg-st-run-bg text-st-run">
      new
    </Badge>
  )
}

export function SessionDeletedBadge(): React.ReactElement {
  return (
    <Badge variant="outline" className="border-st-down/40 bg-st-down-bg text-st-down">
      deleted
    </Badge>
  )
}

/** Compact pill for dense list rows — sits at the trailing edge, no row tint. */
export function SessionCompactNewBadge(): React.ReactElement {
  return (
    <Badge
      variant="outline"
      className="shrink-0 border-st-run/40 bg-st-run-bg px-1.5 py-0 text-10 font-semibold text-st-run"
    >
      new
    </Badge>
  )
}

/** Compact deleted pill — matches {@link SessionCompactNewBadge} density. */
export function SessionCompactDeletedBadge(): React.ReactElement {
  return (
    <Badge
      variant="outline"
      className="shrink-0 border-st-down/40 bg-st-down-bg px-1.5 py-0 text-10 font-semibold text-st-down"
    >
      deleted
    </Badge>
  )
}

/** Compact updated pill — matches {@link SessionCompactNewBadge} density. */
export function SessionCompactUpdatedBadge(): React.ReactElement {
  return (
    <Badge
      variant="outline"
      className="shrink-0 border-st-warn/40 bg-st-warn-bg px-1.5 py-0 text-10 font-semibold text-st-warn"
    >
      updated
    </Badge>
  )
}

export function SessionOtherSectionBadge(): React.ReactElement {
  return (
    <Badge
      variant="outline"
      className="shrink-0 border-line bg-bg-sunk px-1.5 py-0 text-10 font-normal text-ink-4"
    >
      other
    </Badge>
  )
}

export function SessionUpdatedBadge(): React.ReactElement {
  return (
    <Badge variant="outline" className="border-st-warn/40 bg-st-warn-bg text-st-warn">
      updated
    </Badge>
  )
}

/** Compact presence dot for Plant tab bar when a tab has session paint. */
export function SessionTouchedDot(): React.ReactElement {
  return <span className="inline-block size-1.5 shrink-0 rounded-full bg-st-warn" aria-hidden />
}

/**
 * Parent/group chrome for descendant session paint: word pills for
 * `new` / `updated` / `deleted` (never a bare amber dot on content surfaces).
 */
export function SessionDescendantPaintMarks({
  ids,
  markFor,
  hasGhosts = false
}: {
  ids: readonly string[]
  markFor: (id: string) => PlantSessionMarkView | undefined
  /** When true, surface a deleted pill for ghost descendants. */
  hasGhosts?: boolean
}): React.ReactElement | null {
  const added = countSessionMarks(ids, markFor, 'added')
  const touched = countSessionMarks(ids, markFor, 'touched')
  const deleted = countSessionMarks(ids, markFor, 'deleted')
  if (added <= 0 && touched <= 0 && deleted <= 0 && !hasGhosts) return null
  return (
    <span className="inline-flex shrink-0 items-center gap-1 self-center">
      {added > 0 ? <SessionCompactNewBadge /> : null}
      {touched > 0 ? <SessionCompactUpdatedBadge /> : null}
      {deleted > 0 || hasGhosts ? <SessionCompactDeletedBadge /> : null}
    </span>
  )
}

/** Count ids whose session mark matches (for live child lists on Plant cards). */
export function countSessionMarks(
  ids: readonly string[],
  markFor: (id: string) => PlantSessionMarkView | undefined,
  mark: Exclude<PlantSessionMarkView, 'dimmed'>
): number {
  let count = 0
  for (const id of ids) {
    if (markFor(id) === mark) count += 1
  }
  return count
}

/**
 * Changelist pill: always its own bubble. `+a` green, `~m` orange, `−d` red.
 * Prefer this over inlining deltas inside count badges.
 */
export function SessionRollupPill({
  count,
  added,
  deleted = 0,
  modified = 0
}: {
  /** Added count (legacy rollup call sites). */
  count?: number
  /** Added count (preferred when pairing with deleted/modified). */
  added?: number
  deleted?: number
  /** Touched / property-updated count. */
  modified?: number
}): React.ReactElement | null {
  const addedCount = added ?? count ?? 0
  if (addedCount <= 0 && deleted <= 0 && modified <= 0) return null
  return (
    <Badge
      variant="outline"
      className="inline-flex shrink-0 items-center gap-1 border-sess/40 bg-sess-bg px-1.5 py-0 text-10 font-semibold tabular-nums"
    >
      {addedCount > 0 ? <span className="text-st-run">+{addedCount}</span> : null}
      {modified > 0 ? <span className="text-st-warn">~{modified}</span> : null}
      {deleted > 0 ? <span className="text-st-down">−{deleted}</span> : null}
    </Badge>
  )
}

/** Detail-page title mark: short pill, not the longer session-chip copy. */
export function SessionTitleMark({
  mark
}: {
  mark: PlantSessionMarkView | undefined
}): React.ReactElement | null {
  if (mark === 'added') return <SessionNewBadge />
  if (mark === 'touched') return <SessionUpdatedBadge />
  if (mark === 'dimmed') return <SessionOtherSectionBadge />
  return null
}

/** Name row / card mark: word pill (`new` / `updated` / `other`). */
export function SessionMarkAffordances({
  mark
}: {
  mark: PlantSessionMarkView | undefined
}): React.ReactElement | null {
  return <SessionTitleMark mark={mark} />
}

/** Trailing mark for dense lists: compact word pills, no row fill. */
export function SessionRowEndMark({
  mark
}: {
  mark: PlantSessionMarkView | undefined
}): React.ReactElement | null {
  if (mark === 'added') return <SessionCompactNewBadge />
  if (mark === 'touched') return <SessionCompactUpdatedBadge />
  if (mark === 'dimmed') return <SessionOtherSectionBadge />
  return null
}

/**
 * Paint-aware trailing mark for entity rows and chips. Entity-to-session lookup
 * stays here so every equipment surface uses the same paint source and style.
 */
export function SessionEntityMark({
  entityId,
  parentEntityId
}: {
  entityId: string
  /** Suppresses a redundant `new` pill when an added child sits under an added parent. */
  parentEntityId?: string
}): React.ReactElement | null {
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(entityId)
  const parentMark = parentEntityId ? markFor(parentEntityId) : undefined
  const visibleMark = parentMark === 'added' && mark === 'added' ? undefined : mark

  return <SessionRowEndMark mark={visibleMark} />
}

/** Current breadcrumb crumb — same trailing mark as parent links in the chain. */
export function SessionBreadcrumbCurrent({
  entityId,
  children
}: {
  entityId: string
  children: React.ReactNode
}): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-1.5 text-ink">
      {children}
      <SessionEntityMark entityId={entityId} />
    </span>
  )
}

/** Non-navigating ghost tile for deleted lines / equipment / sublines. */
export function SessionGhostTile({
  name,
  subtitle,
  icon,
  view = 'deleted'
}: {
  name: string
  /** Optional secondary line under the name (not used for delete status — use the pill). */
  subtitle?: string
  icon?: React.ReactNode
  view?: Extract<PlantSessionMarkView, 'deleted' | 'dimmed'>
}): React.ReactElement {
  const dimmed = view === 'dimmed'
  return (
    <div
      className={cn(
        'flex min-w-0 cursor-not-allowed items-center gap-2.5 rounded-lg border p-2.5 text-card-foreground shadow-sm',
        dimmed ? 'border-line bg-bg-sunk/70' : 'border-st-down/40 bg-st-down-bg'
      )}
      title={dimmed ? 'changed by another section' : 'deleted this session'}
    >
      {icon}
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className={cn(
              'block min-w-0 flex-1 truncate text-13 font-semibold line-through',
              dimmed ? 'text-ink-4' : 'text-st-down'
            )}
          >
            {name}
          </span>
          {dimmed ? <SessionOtherSectionBadge /> : <SessionDeletedBadge />}
        </span>
        {subtitle ? (
          <span
            className={cn(
              'block truncate font-mono text-11',
              dimmed ? 'text-ink-4' : 'text-st-down/80'
            )}
          >
            {subtitle}
          </span>
        ) : null}
      </span>
    </div>
  )
}

/** Non-navigating struck row for deleted peers / tags. */
export function SessionGhostRow({
  name,
  className,
  view = 'deleted'
}: {
  name: string
  className?: string
  view?: Extract<PlantSessionMarkView, 'deleted' | 'dimmed'>
}): React.ReactElement {
  const dimmed = view === 'dimmed'
  return (
    <div
      className={cn(
        'flex min-w-0 cursor-not-allowed items-center gap-2 rounded-md border px-2.5 py-2',
        dimmed ? 'border-line bg-bg-sunk/70' : 'border-st-down/40 bg-st-down-bg',
        className
      )}
      title={dimmed ? 'changed by another section' : 'deleted this session'}
    >
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-13 font-medium line-through',
          dimmed ? 'text-ink-4' : 'text-st-down'
        )}
      >
        {name}
      </span>
      {dimmed ? <SessionOtherSectionBadge /> : <SessionCompactDeletedBadge />}
    </div>
  )
}

/** Visible deleted children under a container, gated by paint filter. */
export type PlantSessionGhostView = PlantSessionGhost & {
  view: Extract<PlantSessionMarkView, 'deleted' | 'dimmed'>
}

export function useVisibleGhosts(containerId: string, label?: string): PlantSessionGhostView[] {
  const { active, filter, ghostsByContainerId, markFor } = usePlantSessionPaint()
  return React.useMemo(() => {
    if (!active || !filter.enabled) return []
    const ghosts = ghostsByContainerId.get(containerId) ?? []
    const visible: PlantSessionGhostView[] = []
    for (const ghost of ghosts) {
      if (label && !ghost.labels.includes(label)) continue
      const view = markFor(ghost.id)
      if (view !== 'deleted' && view !== 'dimmed') continue
      visible.push({ ...ghost, view })
    }
    return visible
  }, [active, containerId, filter.enabled, ghostsByContainerId, label, markFor])
}

/**
 * Deleted nodes of `label` that have no containment parent bucket (e.g. a
 * deleted root Process). Used to append root-level ghost rows on tree rails.
 */
export function collectUnplacedGhosts(
  markById: ReadonlyMap<string, PlantSessionMarkEntry>,
  ghostsByContainerId: ReadonlyMap<string, readonly PlantSessionGhost[]>,
  markFor: (id: string) => PlantSessionMarkView | undefined,
  options: { label: string; liveIds?: ReadonlySet<string> }
): PlantSessionGhostView[] {
  const placed = new Set<string>()
  for (const ghosts of ghostsByContainerId.values()) {
    for (const ghost of ghosts) {
      if (ghost.labels.includes(options.label)) placed.add(ghost.id)
    }
  }
  const visible: PlantSessionGhostView[] = []
  for (const [id, entry] of markById) {
    if (entry.mark !== 'deleted') continue
    if (!entry.labels.includes(options.label)) continue
    if (options.liveIds?.has(id) || placed.has(id)) continue
    const view = markFor(id)
    if (view !== 'deleted' && view !== 'dimmed') continue
    visible.push({ id, name: entry.name, labels: entry.labels, view })
  }
  return visible
}

/**
 * Deleted children bucketed under any of `containerIds` (e.g. Alarm ghosts
 * under the Tag ids of a UDT instance's live alarms). Dedupes across parents.
 */
export function collectGhostsForContainers(
  containerIds: readonly string[],
  ghostsByContainerId: ReadonlyMap<string, readonly PlantSessionGhost[]>,
  markFor: (id: string) => PlantSessionMarkView | undefined,
  label?: string
): PlantSessionGhostView[] {
  const seen = new Set<string>()
  const visible: PlantSessionGhostView[] = []
  for (const containerId of containerIds) {
    for (const ghost of ghostsByContainerId.get(containerId) ?? []) {
      if (label && !ghost.labels.includes(label)) continue
      if (seen.has(ghost.id)) continue
      const view = markFor(ghost.id)
      if (view !== 'deleted' && view !== 'dimmed') continue
      seen.add(ghost.id)
      visible.push({ ...ghost, view })
    }
  }
  return visible
}

/**
 * Deleted nodes of `label` whose containment parent is not in
 * `knownContainerIds` — e.g. Alarm ghosts on tags that no longer appear
 * in the live alarms index (place under Ungrouped).
 */
export function collectOrphanLabeledGhosts(
  knownContainerIds: ReadonlySet<string>,
  ghostsByContainerId: ReadonlyMap<string, readonly PlantSessionGhost[]>,
  markFor: (id: string) => PlantSessionMarkView | undefined,
  label: string
): PlantSessionGhostView[] {
  const seen = new Set<string>()
  const visible: PlantSessionGhostView[] = []
  for (const [containerId, ghosts] of ghostsByContainerId) {
    if (knownContainerIds.has(containerId)) continue
    for (const ghost of ghosts) {
      if (!ghost.labels.includes(label)) continue
      if (seen.has(ghost.id)) continue
      const view = markFor(ghost.id)
      if (view !== 'deleted' && view !== 'dimmed') continue
      seen.add(ghost.id)
      visible.push({ ...ghost, view })
    }
  }
  return visible
}

/** True when any id carries an added/touched session mark (chrome dots). */
export function hasSessionPaintMarks(
  ids: readonly string[],
  markFor: (id: string) => PlantSessionMarkView | undefined
): boolean {
  return (
    countSessionMarks(ids, markFor, 'added') > 0 || countSessionMarks(ids, markFor, 'touched') > 0
  )
}

/** Ghost relationships whose surviving endpoint is `entityId`. */
export function useVisibleGhostRels(entityId: string): Array<{
  relId: string
  type: string
  direction: 'out' | 'in'
  peerId: string
  peerName: string
  peerLabels: string[]
  view: Extract<PlantSessionMarkView, 'deleted' | 'dimmed'>
}> {
  const { active, filter, ghostRels, markById, relationshipMarkFor } = usePlantSessionPaint()
  return React.useMemo(() => {
    if (!active || !filter.enabled) return []
    const rows: Array<{
      relId: string
      type: string
      direction: 'out' | 'in'
      peerId: string
      peerName: string
      peerLabels: string[]
      view: Extract<PlantSessionMarkView, 'deleted' | 'dimmed'>
    }> = []
    for (const rel of ghostRels) {
      const onStart = rel.startId === entityId
      const onEnd = rel.endId === entityId
      if (!onStart && !onEnd) continue
      const peerId = onStart ? rel.endId : rel.startId
      const peerEntry = markById.get(peerId)
      const view = relationshipMarkFor(rel.id)
      if (view !== 'deleted' && view !== 'dimmed') continue
      rows.push({
        relId: rel.id,
        type: rel.type,
        direction: onStart ? 'out' : 'in',
        peerId,
        peerName: peerEntry?.name ?? peerId,
        peerLabels: peerEntry?.labels ?? [],
        view
      })
    }
    return rows
  }, [active, entityId, filter.enabled, ghostRels, markById, relationshipMarkFor])
}
